import {
  useUpdateMarketingOptInMutation,
  useUpdateUserNameMutation,
} from '@/app/redux/endpoints/user';
import { useUser } from '@/app/redux/hooks/user';
import InitialsCircle from '@/common/ui/InitialsCircle';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { handleLogout } from '@/common/utils/auth';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import api from '@/db/apiClient';
import {
  EnvelopeSimpleIcon,
  EyeIcon,
  EyeSlashIcon,
  IdentificationCardIcon,
  KeyIcon,
  SignOutIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { Input } from '@shadcn/components/input';
import { Label } from '@shadcn/components/label';
import { Separator } from '@shadcn/components/separator';
import { signInWithEmailAndPassword, updatePassword } from 'firebase/auth';
import React, { useState } from 'react';
import { toast } from 'sonner';
import styled from 'styled-components';
import {
  SettingsContentContainer,
  SettingsHeaderContainer,
  SettingsHeaderLeft,
  SettingsHeaderText,
  SettingsPageContainer,
  SettingsSectionHeader,
} from './SettingsPageStyles';

export default function Account() {
  const { user } = useUser();
  const [updateUserName] = useUpdateUserNameMutation();
  const [updateMarketingOptIn] = useUpdateMarketingOptInMutation();

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUpdatingOptIn, setIsUpdatingOptIn] = useState(false);

  const [name, setName] = useState(user?.name || '');

  const handleOptInChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = event.target.checked;
    try {
      if (!user) {
        throw new Error('No user found');
      }
      await updateMarketingOptIn({
        userId: user.uid,
        marketingOptIn: checked,
      }).unwrap();

      trackMixpanelEvent('User Marketing Preference Changed', {
        optedIn: checked,
      });

      toast.success('Preferences updated');
    } catch (error) {
      console.error('Error updating marketing preferences:', error);
      toast.error('Failed to update preferences', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    } finally {
      setIsUpdatingOptIn(false);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleNameBlur = async () => {
    if (!user) {
      console.error('No user found');
      toast.error('Failed to update name', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }

    if (name !== user.name) {
      try {
        const oldName = user.name;
        await updateUserName({ userId: user.uid, name }).unwrap();

        trackMixpanelEvent('User Name Changed', {
          oldName,
          newName: name,
        });

        toast.success('Name updated');
      } catch (error) {
        console.error('Error updating name:', error);
        toast.error('Failed to update name', {
          description: UNKNOWN_ERROR_MESSAGE,
        });
        setName(user.name || '');
      }
    }
  };

  const handleResetPassword = () => {
    setPasswordModalOpen(true);
  };

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setIsLoading(false);
  };

  const handleCloseModal = () => {
    setPasswordModalOpen(false);
    resetForm();
  };

  const handleUpdatePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Failed to update password', {
        description: 'New passwords do not match',
      });
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Failed to update password', {
        description: 'Password must be at least 6 characters long',
      });
      return;
    }

    setIsLoading(true);
    const firebaseUser = api.asEducator.auth.currentUser;

    try {
      // First reauthenticate the user with their current password
      if (firebaseUser?.email) {
        await signInWithEmailAndPassword(
          api.asEducator.auth,
          firebaseUser.email,
          currentPassword,
        );
      }

      // Then update the password
      if (firebaseUser) {
        await updatePassword(firebaseUser, newPassword);
        toast.success('Password updated');

        trackMixpanelEvent('User Password Changed');
        handleCloseModal();
      }
    } catch (error: any) {
      console.error('Error updating password:', error);
      if (error.code === 'auth/wrong-password') {
        toast.error('Failed to update password', {
          description: 'Current password is incorrect',
        });
      } else if (error.code === 'auth/requires-recent-login') {
        toast.error('Failed to update password', {
          description: 'Please sign in again before updating your password',
        });
        handleLogout();
      } else {
        toast.error('Failed to update password', {
          description: UNKNOWN_ERROR_MESSAGE,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendWelcomeEmail = async () => {
    await api.asEducator.sendWelcomeEmail({ uid: user?.uid || '' });
  };

  const handleTriggerTrialEndHandler = async () => {
    await api.asEducator.triggerTrialEndHandler();
  };

  return (
    <SettingsPageContainer>
      <SettingsHeaderContainer>
        <SettingsHeaderLeft>
          <InitialsCircle
            userName={user?.name || user?.email || 'User'}
            size={60}
          />
          <SettingsHeaderText>
            <h2>Profile</h2>
            <div>Manage your personal details</div>
          </SettingsHeaderText>
        </SettingsHeaderLeft>

        <Button
          variant="outline"
          onClick={handleLogout}
          className="flex items-center gap-2"
        >
          <SignOutIcon className="h-4 w-4" />
          Sign Out
        </Button>
      </SettingsHeaderContainer>

      <SettingsContentContainer>
        <div>
          <SettingsSectionHeader>Personal Details</SettingsSectionHeader>
          <FormGroup>
            <Label className="flex items-center gap-2 text-muted-foreground text-xs">
              <IdentificationCardIcon size={16} />
              Name
            </Label>
            <Input
              value={name}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              placeholder="Enter your name"
            />
          </FormGroup>
        </div>

        <Separator />

        {process.env.NODE_ENV === 'development' && (
          <div className="flex flex-col gap-8">
            <div>
              <SettingsSectionHeader>Development Tools</SettingsSectionHeader>
              <DevButtonsContainer>
                <Button
                  variant="outline"
                  onClick={handleSendWelcomeEmail}
                  loading={isLoading}
                >
                  Send welcome email
                </Button>

                <Button
                  variant="outline"
                  onClick={handleTriggerTrialEndHandler}
                  loading={isLoading}
                >
                  Trigger trial end handler
                </Button>
              </DevButtonsContainer>
            </div>

            <Separator />
          </div>
        )}

        <div>
          <SettingsSectionHeader>Account Details</SettingsSectionHeader>
          <FormGroup>
            <Label className="flex items-center gap-2 text-muted-foreground text-xs">
              <EnvelopeSimpleIcon size={16} />
              Email Address
            </Label>
            <Input value={user?.email || ''} readOnly className="bg-muted" />
          </FormGroup>
          <FormGroup>
            <Label className="flex items-center gap-2 text-muted-foreground text-xs mt-4">
              <KeyIcon size={16} />
              Password
            </Label>
            <div className="flex gap-2">
              <Input
                type="password"
                value="••••••••"
                readOnly
                className="font-mono"
              />
              <Button
                variant="outline"
                className="min-w-[140px]"
                onClick={handleResetPassword}
              >
                Change password
              </Button>
            </div>
          </FormGroup>
          <FormGroup>
            <MarketingOptIn>
              <div className="flex items-start space-x-4 space-y-0">
                <input
                  type="checkbox"
                  checked={user?.marketingOptIn || false}
                  onChange={handleOptInChange}
                  disabled={isUpdatingOptIn}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="marketing"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Spellcaster Updates & Events Newsletter
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Stay informed about product updates, free events, and
                    educational resources.
                  </p>
                </div>
              </div>
            </MarketingOptIn>
          </FormGroup>
        </div>
      </SettingsContentContainer>

      <Dialog open={passwordModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <FormGroup>
              <Label htmlFor="current-password">Current password</Label>
              <PasswordInputWrapper>
                <Input
                  id="current-password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? (
                    <EyeIcon size={20} />
                  ) : (
                    <EyeSlashIcon size={20} />
                  )}
                </PasswordToggle>
              </PasswordInputWrapper>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="new-password">New password</Label>
              <PasswordInputWrapper>
                <Input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeIcon size={20} />
                  ) : (
                    <EyeSlashIcon size={20} />
                  )}
                </PasswordToggle>
              </PasswordInputWrapper>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="confirm-password">Confirm new password</Label>
              <PasswordInputWrapper>
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeIcon size={20} />
                  ) : (
                    <EyeSlashIcon size={20} />
                  )}
                </PasswordToggle>
              </PasswordInputWrapper>
            </FormGroup>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseModal}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdatePassword}
              disabled={
                isLoading ||
                !currentPassword ||
                !newPassword ||
                !confirmPassword
              }
            >
              {isLoading ? 'Updating...' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsPageContainer>
  );
}

const FormGroup = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0.25rem;
  color: hsl(var(--muted-foreground));
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: hsl(var(--foreground));
  }
`;

const DevButtonsContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const MarketingOptIn = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: hsl(var(--muted));
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: hsl(var(--muted) / 0.8);
  }

  label {
    cursor: pointer;
  }

  .text-muted-foreground {
    color: hsl(var(--muted-foreground));
  }
`;
